/**
 * useFollowing Hook (Shared)
 * Get users that a user follows (by displayName)
 * Platform-agnostic — uses HttpClient interface
 */

import { useCallback, useEffect, useState } from 'react';
import type { HttpClient } from '../api/httpClient';
import { ApiError } from '../api/httpClient';
import type { PaginatedUserList, SearchResult } from '../socialTypes';

// =============================================================================
// Types
// =============================================================================

export interface UseFollowingOptions {
  httpClient: HttpClient;
  displayName: string | undefined;
}

export interface UseFollowingResult {
  following: SearchResult[];
  loading: boolean;
  error: string | null;
  total: number;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  reload: () => void;
}

const PAGE_SIZE = 20;

// =============================================================================
// Hook
// =============================================================================

export function useFollowing({ httpClient, displayName }: UseFollowingOptions): UseFollowingResult {
  const [following, setFollowing] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);

  const loadFollowing = useCallback(
    async (currentOffset: number, append: boolean = false) => {
      if (!displayName) return;

      setLoading(true);
      setError(null);

      try {
        const data = await httpClient.get<PaginatedUserList>(
          `/api/users/${encodeURIComponent(displayName)}/following`,
          { limit: PAGE_SIZE, offset: currentOffset },
        );

        setTotal(data.total);
        setFollowing((prev) => (append ? [...prev, ...data.users] : data.users));
        setOffset(currentOffset);
      } catch (err) {
        const errorMsg = ApiError.getUserMessage(err, 'Failed to load following');
        setError(errorMsg);
        console.error('Failed to load following:', err);
      } finally {
        setLoading(false);
      }
    },
    [displayName, httpClient],
  );

  // Load initial following when displayName changes
  useEffect(() => {
    if (displayName) {
      setFollowing([]);
      setOffset(0);
      loadFollowing(0, false);
    }
  }, [displayName, loadFollowing]);

  const loadMore = useCallback(async () => {
    const nextOffset = offset + PAGE_SIZE;
    await loadFollowing(nextOffset, true);
  }, [offset, loadFollowing]);

  const reload = useCallback(() => {
    setFollowing([]);
    setOffset(0);
    loadFollowing(0, false);
  }, [loadFollowing]);

  const hasMore = following.length < total;

  return {
    following,
    loading,
    error,
    total,
    hasMore,
    loadMore,
    reload,
  };
}
